/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useState } from 'react'
import { Form, Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import FormDateInput from '../atoms/FormDateInput'
import FormSelect from '../atoms/FormSelect'
import { FormFieldError, FormFieldLabel, FormTextInput, fieldBoxShadow } from '../atoms/FormTextInput'
import InsetSegmentedControl from '../atoms/InsetSegmentedControl'
import PlusSvg from '../other/svg/PlusSvg'
import { formatClientDetailsName } from 'src/helpers/formatClientDetailsName'
import ClientSearchSelect from './ClientSearchSelect'
import FormFooter from './FormFooter'
import PriceBreakdownCard from './PriceBreakdownCard'
import SellContractAgreementSection from './SellContractAgreementSection'
import SellContractConditionSection from './SellContractConditionSection'
import SellContractDeliverySection from './SellContractDeliverySection'
import SellContractSegmentField from './SellContractSegmentField'
import SellContractServicesSection from './SellContractServicesSection'
import VehicleSearchSelect from './VehicleSearchSelect'
import { formatPrice } from 'src/helpers/formatPrice'
import { SellContractFormData, validateSellContractForm } from 'src/helpers/sellContractFormValidation'
import { FETCH_MOTORBIKE, MarketPlaceMotorbike } from 'src/redux/motorbikesReducer'
import { RootReducer } from 'src/redux/rootReducer'
import { Client as PickerClient } from 'src/redux/clientsReducer'
import { Client as ClientDetails } from 'src/redux/clientReducer'

interface SellContractFormProps {
  initialValues: Partial<SellContractFormData>
  onSubmit: (values: SellContractFormData) => void
  onCancel: () => void
  loading?: boolean
  mode?: 'create' | 'edit'
}

type PriceType = 'net' | 'gross'

type VatLabelKey =
  | 'sell_contract.vat_23'
  | 'sell_contract.vat_10'
  | 'sell_contract.vat_5'
  | 'sell_contract.vat_marginal'
  | 'sell_contract.vat_small_business'

interface VatOption {
  value: string
  labelKey: VatLabelKey
  rate: number
}

const vatOptions: VatOption[] = [
  { value: 'vat_23', labelKey: 'sell_contract.vat_23', rate: 23 },
  { value: 'vat_10', labelKey: 'sell_contract.vat_10', rate: 10 },
  { value: 'vat_5', labelKey: 'sell_contract.vat_5', rate: 5 },
  { value: 'marginal_taxation', labelKey: 'sell_contract.vat_marginal', rate: 0 },
  { value: 'small_business', labelKey: 'sell_contract.vat_small_business', rate: 0 }
]

const vatRateForOption = (option: string): number => {
  return vatOptions.find((item) => item.value === option)?.rate ?? 0
}

const roundMoney = (value: number): number => Math.round(value * 100) / 100

const toMoneyNumber = (value: string | number | undefined): number => {
  const parsed = Number(value)
  if (Number.isNaN(parsed)) {
    return 0
  }
  return parsed
}

const yearFromRegistration = (firstRegistration: string | null): string => {
  if (firstRegistration === null || firstRegistration.length < 4) {
    return ''
  }
  const year = firstRegistration.slice(0, 4)
  if (!/^\d{4}$/.test(year)) {
    return ''
  }
  return year
}

export const vehicleFieldsFromMotorbike = (
  motorbike: MarketPlaceMotorbike
): Pick<SellContractFormData, 'motorbike_id' | 'motorbike_name' | 'vin' | 'motorbike_mileage' | 'motorbike_year'> => {
  return {
    motorbike_id: parseInt(motorbike.id, 10),
    motorbike_name: `${motorbike.brand_name} ${motorbike.model}`.trim(),
    vin: motorbike.vin ?? '',
    motorbike_mileage: motorbike.total_mileage,
    motorbike_year: yearFromRegistration(motorbike.first_registration)
  }
}

interface ClientSummaryFields {
  client_id: number
  client_name: string
  client_email: string
  client_phone: string
  client_address: string
  client_type: 'personal' | 'company'
}

const formatClientAddress = (client: ClientDetails): string => {
  const street = `${client.street} ${client.house_number}`.trim()
  const city = `${client.zip_code} ${client.place}`.trim()
  if (street.length === 0) {
    return city
  }
  if (city.length === 0) {
    return street
  }
  return `${street}, ${city}`
}

const clientParty = (clientType: string | null | undefined): 'personal' | 'company' => {
  return clientType === 'company' ? 'company' : 'personal'
}

export const clientFieldsFromPicker = (client: PickerClient): ClientSummaryFields => {
  return {
    client_id: parseInt(client.id, 10),
    client_name: client.name,
    client_email: client.email,
    client_phone: client.phone_number,
    client_address: '',
    client_type: clientParty(client.client_type)
  }
}

export const clientFieldsFromDetails = (client: ClientDetails): ClientSummaryFields => {
  return {
    client_id: client.id,
    client_name: formatClientDetailsName(client),
    client_email: client.email ?? client.email_address ?? '',
    client_phone: client.phone_number ?? '',
    client_address: formatClientAddress(client),
    client_type: clientParty(client.client_type)
  }
}

const displayOrDash = (value: string | undefined): string => {
  if (typeof value !== 'string' || value.length === 0) {
    return '-'
  }
  return value
}

const mileageDisplayValue = (mileage: number | null | undefined, kmLabel: string): string => {
  if (typeof mileage !== 'number') {
    return '-'
  }
  return `${formatPrice(mileage)} ${kmLabel}`
}

const monthFromDate = (value: string | null | undefined): string => {
  if (value === null || typeof value === 'undefined' || value.length < 7) {
    return ''
  }
  return value.slice(0, 7)
}

interface SelectedRecordSummaryProps {
  title: string
  lines: string[]
}

const SelectedRecordSummary = ({ title, lines }: SelectedRecordSummaryProps): JSX.Element => {
  return (
    <div className="self-stretch inline-flex justify-start items-start gap-6">
      <div className="flex-1 inline-flex flex-col justify-start items-start gap-3">
        <div className="self-stretch flex flex-col justify-center items-start gap-1">
          <div className="self-stretch inline-flex justify-between items-center">
            <div className="text-foregrounds-fg-base text-xl font-semibold font-Montserrat leading-5">{title}</div>
          </div>
          {lines.map((line, index) => (
            <div
              key={`${index}-${line}`}
              className="text-foregrounds-fg-subtle text-l font-medium font-Montserrat leading-4">
              {line}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

interface VehicleRecordSummaryProps {
  motorbikeId: number
  title: string
  vin: string
  mileage: string
  year: string
}

const VehicleRecordSummary = ({ motorbikeId, title, vin, mileage, year }: VehicleRecordSummaryProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const motorbikeKey = String(motorbikeId)
  const nextHu = useSelector((state: RootReducer) => {
    const { motorbike, motorbikeRequestId } = state.motorbikesReducer
    if (motorbike === null || motorbikeRequestId !== motorbikeKey) {
      return null
    }
    return motorbike.next_hu
  })

  useEffect(() => {
    dispatch(FETCH_MOTORBIKE(motorbikeKey))
  }, [dispatch, motorbikeKey])

  const month = monthFromDate(nextHu)

  return (
    <SelectedRecordSummary
      title={title}
      lines={[
        t('sell_contract.vin_value', { vin }),
        t('sell_contract.mileage_value', { mileage }),
        t('sell_contract.year_value', { year }),
        `${t('single_offer.motorbike_next_hu')}: ${month.length === 0 ? '-' : month}`
      ]}
    />
  )
}

interface FieldMeta {
  error?: string
  touched?: boolean
  submitFailed?: boolean
}

interface NetPriceSyncProps {
  netPrice: number
  currentNetPrice: number
  onChange: (value: number) => void
}

const isFieldInvalid = (meta: FieldMeta, submitFailed = false): boolean =>
  Boolean(meta.error) && (Boolean(meta.touched) || Boolean(meta.submitFailed) || submitFailed)

const currencySymbol = (currency: string | undefined): string => {
  if (currency === 'PLN') {
    return 'zł'
  }
  return '€'
}

interface PriceBreakdown {
  netPrice: number
  grossPrice: number
  vatAmount: number
  vatRate: number
  priceType: PriceType
}

const priceBreakdown = (values: Partial<SellContractFormData>): PriceBreakdown => {
  const price = toMoneyNumber(values.price)
  const vatRate = vatRateForOption(values.vat_option ?? '')
  const priceType: PriceType = values.price_type ?? 'gross'
  const netPrice = priceType === 'gross' ? roundMoney(price / (1 + vatRate / 100)) : price
  const grossPrice = priceType === 'net' ? roundMoney(price * (1 + vatRate / 100)) : price
  return {
    netPrice,
    grossPrice,
    vatAmount: roundMoney(grossPrice - netPrice),
    vatRate,
    priceType
  }
}

const hasSelectedRecord = (id: number | undefined, label: string): boolean => {
  return typeof id === 'number' && id !== 0 && label.length > 0
}

const clientSummaryLinesFor = (
  values: Partial<SellContractFormData>,
  t: (key: string, options?: Record<string, string>) => string
): string[] => {
  const lines = [
    t('sell_contract.email_value', { email: displayOrDash(values.client_email) }),
    t('sell_contract.phone_value', { phone: displayOrDash(values.client_phone) })
  ]
  const clientAddress = displayOrDash(values.client_address)
  if (clientAddress !== '-') {
    lines.push(t('sell_contract.address_value', { address: clientAddress }))
  }
  return lines
}

const fieldClass = 'self-stretch flex flex-col justify-start items-start gap-2'
const numericPricePattern = /^\d*[.,]?\d*$/
const cardShadow =
  '0px 4px 8px 0px rgba(0,0,0,0.08), 0px 2px 4px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const NetPriceSync = ({ netPrice, currentNetPrice, onChange }: NetPriceSyncProps): null => {
  useEffect(() => {
    if (currentNetPrice !== netPrice) {
      onChange(netPrice)
    }
  }, [currentNetPrice, netPrice, onChange])

  return null
}

const SellContractForm = ({
  initialValues,
  onSubmit,
  onCancel,
  loading = false,
  mode = 'create'
}: SellContractFormProps): JSX.Element => {
  const { t } = useTranslation()
  const [hasDownPayment, setHasDownPayment] = useState(
    typeof initialValues.down_payment === 'string' &&
      initialValues.down_payment.length > 0 &&
      Number(initialValues.down_payment) !== 0
  )

  return (
    <Form
      onSubmit={onSubmit}
      validate={validateSellContractForm}
      initialValues={initialValues}
      render={({ handleSubmit, submitting, dirty, values, form, submitFailed }) => {
        const { netPrice, grossPrice, vatAmount, vatRate, priceType } = priceBreakdown(values)
        const selectedCurrencySymbol = currencySymbol(values.currency)
        const vehicleLabel = values.motorbike_name ?? ''
        const clientLabel = values.client_name ?? ''
        const hasSelectedVehicle = hasSelectedRecord(values.motorbike_id, vehicleLabel)
        const hasSelectedClient = hasSelectedRecord(values.client_id, clientLabel)
        const showError = (meta: FieldMeta): boolean => isFieldInvalid(meta, submitFailed)
        const clientSummaryLines = clientSummaryLinesFor(values, t)

        return (
          <form
            onSubmit={handleSubmit}
            className="self-stretch flex-1 relative bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-center overflow-hidden"
            style={{ boxShadow: cardShadow }}>
            <NetPriceSync
              netPrice={netPrice}
              currentNetPrice={toMoneyNumber(values.net_price)}
              onChange={(value) => {
                form.change('net_price', value)
              }}
            />
            <div className="w-full max-w-[1000px] min-w-[720px] px-4 pt-4 pb-6 flex-1 flex flex-col justify-start items-start gap-6 overflow-y-auto">
              <div className="text-zinc-900 text-xl font-bold font-Montserrat leading-7">
                {t('sell_contract.contract_data')}
              </div>

              <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch inline-flex justify-start items-start gap-8">
                  <div className="w-[460px] flex flex-col justify-start items-start gap-6">
                    <Field name="motorbike_id">
                      {({ meta }) => {
                        const selectedVehicleId =
                          typeof values.motorbike_id === 'number' && values.motorbike_id !== 0
                            ? String(values.motorbike_id)
                            : ''
                        return (
                          <div className={`self-stretch ${fieldClass}`}>
                            <FormFieldLabel label={t('sell_contract.vehicle')} />
                            <VehicleSearchSelect
                              selectedId={selectedVehicleId}
                              selectedLabel={vehicleLabel}
                              placeholder={t('sell_contract.select')}
                              error={showError(meta)}
                              onSelect={(motorbike) => {
                                const fields = vehicleFieldsFromMotorbike(motorbike)
                                form.change('motorbike_id', fields.motorbike_id)
                                form.change('motorbike_name', fields.motorbike_name)
                                form.change('vin', fields.vin)
                                form.change('motorbike_mileage', fields.motorbike_mileage)
                                form.change('motorbike_year', fields.motorbike_year)
                              }}
                              onClear={() => {
                                form.change('motorbike_id', 0)
                                form.change('motorbike_name', '')
                                form.change('vin', '')
                                form.change('motorbike_mileage', null)
                                form.change('motorbike_year', '')
                              }}
                            />
                            <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                          </div>
                        )
                      }}
                    </Field>
                    {hasSelectedVehicle ? (
                      <VehicleRecordSummary
                        motorbikeId={values.motorbike_id ?? 0}
                        title={displayOrDash(vehicleLabel)}
                        vin={displayOrDash(values.vin)}
                        mileage={mileageDisplayValue(values.motorbike_mileage, t('my_vehicles.km'))}
                        year={displayOrDash(values.motorbike_year)}
                      />
                    ) : null}
                  </div>
                  <div className="flex-1 flex flex-col justify-start items-start gap-6">
                    <Field name="client_id">
                      {({ meta }) => {
                        const selectedClientId =
                          typeof values.client_id === 'number' && values.client_id !== 0 ? String(values.client_id) : ''
                        return (
                          <div className={`self-stretch ${fieldClass}`}>
                            <FormFieldLabel label={t('sell_contract.client')} />
                            <ClientSearchSelect
                              selectedId={selectedClientId}
                              selectedLabel={clientLabel}
                              placeholder={t('sell_contract.select')}
                              error={showError(meta)}
                              onSelect={(client) => {
                                const fields = clientFieldsFromPicker(client)
                                form.change('client_id', fields.client_id)
                                form.change('client_name', fields.client_name)
                                form.change('client_email', fields.client_email)
                                form.change('client_phone', fields.client_phone)
                                form.change('client_address', fields.client_address)
                                form.change('client_type', fields.client_type)
                              }}
                              onClear={() => {
                                form.change('client_id', 0)
                                form.change('client_name', '')
                                form.change('client_email', '')
                                form.change('client_phone', '')
                                form.change('client_address', '')
                                form.change('client_type', '')
                              }}
                            />
                            <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                          </div>
                        )
                      }}
                    </Field>
                    {hasSelectedClient ? (
                      <SelectedRecordSummary title={displayOrDash(clientLabel)} lines={clientSummaryLines} />
                    ) : null}
                  </div>
                </div>
                <Field name="language">
                  {({ input, meta }) => (
                    <div className={`w-[460px] ${fieldClass}`}>
                      <FormFieldLabel label={t('sell_contract.contract_language')} />
                      <FormSelect
                        value={input.value ?? ''}
                        onChange={input.onChange}
                        options={[
                          { value: 'de', label: t('sell_contract.language_de') },
                          { value: 'pl', label: t('sell_contract.language_pl') }
                        ]}
                        placeholder={t('sell_contract.select')}
                        error={showError(meta)}
                        clearable={false}
                      />
                      <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                    </div>
                  )}
                </Field>
              </div>

              <div className="self-stretch h-px bg-borders-border-base" />

              <div className="self-stretch flex flex-col justify-start items-start gap-4">
                <div className="self-stretch inline-flex justify-start items-start gap-8">
                  <div className="flex-1 flex justify-start items-center gap-4">
                    <Field name="currency">
                      {({ input, meta }) => (
                        <div className={`flex-1 ${fieldClass}`}>
                          <FormFieldLabel label={t('sell_contract.currency')} />
                          <FormSelect
                            value={input.value ?? ''}
                            onChange={input.onChange}
                            options={[
                              { value: 'EUR', label: t('sell_contract.eur') },
                              { value: 'PLN', label: t('sell_contract.pln') }
                            ]}
                            placeholder={t('sell_contract.select')}
                            clearable={false}
                            error={showError(meta)}
                          />
                          <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                        </div>
                      )}
                    </Field>
                    <Field name="vat_option">
                      {({ input, meta }) => (
                        <div className={`flex-1 ${fieldClass}`}>
                          <FormFieldLabel label={t('sell_contract.vat_rate')} />
                          <FormSelect
                            value={input.value ?? ''}
                            onChange={input.onChange}
                            options={vatOptions.map((option) => ({
                              value: option.value,
                              label: t(option.labelKey)
                            }))}
                            placeholder={t('sell_contract.select')}
                            error={showError(meta)}
                          />
                          <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                        </div>
                      )}
                    </Field>
                  </div>
                  <Field name="price">
                    {({ input, meta }) => (
                      <div className={`flex-1 ${fieldClass}`}>
                        <FormFieldLabel label={t('sell_contract.price')} />
                        <div
                          className="self-stretch h-11 pl-4 pr-1 py-1 bg-backgrounds-bg-field-component rounded-md inline-flex justify-center items-center gap-1.5 overflow-hidden"
                          style={{
                            boxShadow: fieldBoxShadow(Boolean(meta.error) && (Boolean(meta.touched) || submitFailed))
                          }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={input.value ?? ''}
                            onChange={(event) => {
                              const nextValue = event.target.value
                              if (numericPricePattern.test(nextValue)) {
                                input.onChange(nextValue.replace(',', '.'))
                              }
                            }}
                            className="flex-1 h-full bg-transparent outline-none text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5"
                          />
                          <InsetSegmentedControl
                            className="shrink-0"
                            value={priceType}
                            onChange={(nextType) => {
                              form.change('price_type', nextType)
                            }}
                            options={[
                              { value: 'gross', label: t('sell_contract.gross') },
                              { value: 'net', label: t('sell_contract.net') }
                            ]}
                          />
                        </div>
                        <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                      </div>
                    )}
                  </Field>
                </div>

                <PriceBreakdownCard
                  netPrice={netPrice}
                  vatAmount={vatAmount}
                  vatRate={vatRate}
                  grossPrice={grossPrice}
                  currencySymbol={selectedCurrencySymbol}
                />
                <SellContractServicesSection
                  services={values.additional_services ?? []}
                  bikeGross={grossPrice}
                  currencySymbol={selectedCurrencySymbol}
                  form={form}
                  showError={showError}
                />
              </div>

              <div className="self-stretch inline-flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => {
                    if (hasDownPayment) {
                      setHasDownPayment(false)
                      form.change('down_payment', '0')
                      form.change('down_payment_method', '')
                      form.change('down_payment_type', '')
                      form.change('down_payment_date', '')
                      return
                    }
                    setHasDownPayment(true)
                    if (values.down_payment === '0') {
                      form.change('down_payment', '')
                    }
                  }}
                  className="p-2 bg-backgrounds-bg-base rounded-lg inline-flex justify-start items-center gap-2 overflow-hidden"
                  style={{ boxShadow: fieldBoxShadow(false) }}>
                  <div className="size-5 relative flex justify-center items-center">
                    <div
                      className={`size-3.5 rounded-[3px] ${
                        hasDownPayment ? 'bg-Estigroup-primary' : 'bg-backgrounds-bg-component'
                      }`}
                      style={{ boxShadow: fieldBoxShadow(false) }}
                    />
                  </div>
                  <div className="text-foregrounds-fg-base text-xs font-semibold font-Montserrat leading-5">
                    {t('sell_contract.down_payment')}
                  </div>
                </button>
              </div>

              {hasDownPayment && (
                <div className="self-stretch grid grid-cols-2 gap-4">
                  <Field name="down_payment">
                    {({ input, meta }) => (
                      <div className={fieldClass}>
                        <FormFieldLabel label={t('sell_contract.down_payment_amount')} />
                        <FormTextInput
                          value={input.value?.toString() ?? ''}
                          onChange={input.onChange}
                          placeholder="0.00"
                          error={showError(meta)}
                        />
                        <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                      </div>
                    )}
                  </Field>
                  <Field name="down_payment_method">
                    {({ input, meta }) => (
                      <div className={fieldClass}>
                        <FormFieldLabel label={t('sell_contract.payment_method')} />
                        <FormSelect
                          value={input.value ?? ''}
                          onChange={input.onChange}
                          options={[
                            { value: 'bank_transfer', label: t('sell_contract.bank_transfer') },
                            { value: 'instant_transfer', label: t('sell_contract.instant_transfer') },
                            { value: 'cash', label: t('sell_contract.cash') },
                            { value: 'financing', label: t('sell_contract.financing') }
                          ]}
                          placeholder={t('sell_contract.select')}
                          error={showError(meta)}
                        />
                        <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                      </div>
                    )}
                  </Field>
                  <Field name="down_payment_type">
                    {({ input, meta }) => (
                      <div className={fieldClass}>
                        <FormFieldLabel label={t('sell_contract.payment_type')} />
                        <FormSelect
                          value={input.value ?? ''}
                          onChange={input.onChange}
                          options={[
                            { value: 'date', label: t('sell_contract.date') },
                            { value: 'contract', label: t('sell_contract.on_contract') }
                          ]}
                          placeholder={t('sell_contract.select')}
                          error={showError(meta)}
                        />
                        <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                      </div>
                    )}
                  </Field>
                  {values.down_payment_type === 'date' && (
                    <Field name="down_payment_date">
                      {({ input, meta }) => (
                        <div className={fieldClass}>
                          <FormFieldLabel label={t('sell_contract.down_payment_date')} />
                          <FormDateInput
                            value={input.value ?? ''}
                            onChange={input.onChange}
                            onBlur={input.onBlur}
                            placeholder={t('sell_contract.date_placeholder')}
                            error={showError(meta)}
                          />
                          <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                        </div>
                      )}
                    </Field>
                  )}
                </div>
              )}

              <SellContractSegmentField
                name="payment_due_type"
                label={t('sell_contract.payment_due')}
                values={values}
                form={form}
                showError={showError}
                className="w-full"
                fallback="handover"
                required
                options={[
                  { value: 'handover', label: t('sell_contract.payment_due_handover') },
                  { value: 'custom', label: t('sell_contract.payment_due_custom') }
                ]}
              />
              {values.payment_due_type === 'custom' && (
                <Field name="payment_due_text">
                  {({ input, meta }) => (
                    <div className={fieldClass}>
                      <FormFieldLabel label={t('sell_contract.payment_due_text')} />
                      <FormTextInput
                        value={input.value ?? ''}
                        onChange={input.onChange}
                        placeholder={t('sell_contract.payment_due_text')}
                        error={showError(meta)}
                      />
                      <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                    </div>
                  )}
                </Field>
              )}

              <SellContractDeliverySection values={values} form={form} showError={showError} />
              <SellContractConditionSection values={values} form={form} showError={showError} />
              <SellContractAgreementSection values={values} form={form} showError={showError} />
            </div>
            <FormFooter
              cancelLabel={t('my_vehicles.discard')}
              onCancel={onCancel}
              submitLabel={mode === 'edit' ? t('my_vehicles.save') : t('my_vehicles.add')}
              submitDisabled={submitting || loading}
              {...(mode === 'create' ? { submitIcon: <PlusSvg className="w-3.5 h-3.5 text-icon-primary" /> } : {})}
              contentClassName="max-w-[1000px] min-w-[720px]"
              isDirty={dirty}
            />
          </form>
        )
      }}
    />
  )
}

export default SellContractForm
